/**
 * One pull request's Sandboxes v2 preview. The response carries plaintext
 * logins and is `Cache-Control: no-store`, so the query is never persisted and
 * is dropped from memory as soon as the page unmounts.
 *
 * @module features/sandboxes/hooks/useSandboxV2Preview
 */
import { useQuery } from '@tanstack/react-query';
import { getJson } from './useSandboxesV2';
import type { SandboxV2Persona } from './useSandboxV2Detail';

export type SandboxV2PreviewStatus = 'queued' | 'building' | 'ready' | 'failed' | 'destroyed';

export type SandboxV2Preview = {
  applicationId: string;
  prNumber: number;
  status: SandboxV2PreviewStatus;
  headSha: string;
  appliedSha: string | null;
  portUrls: Record<string, string>;
  error: string | null;
  readyMs: number | null;
  leaseExpiresAt: string | null;
  personas: SandboxV2Persona[];
};

export function useSandboxV2Preview(applicationId: string, prNumber: number, enabled: boolean) {
  return useQuery({
    queryKey: ['sandbox-v2-preview', applicationId, prNumber],
    queryFn: async () => {
      const params = new URLSearchParams({ application: applicationId, pr: String(prNumber) });
      return (await getJson<{ data: SandboxV2Preview }>(`/org/sandboxes-v2/previews?${params}`, 'Failed to load the preview')).data;
    },
    enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    // Building previews finish on their own; follow them.
    refetchInterval: (query) =>
      query.state.data?.status === 'queued' || query.state.data?.status === 'building' ? 5000 : false,
  });
}
